import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import findTicketStore from 'entities/beket/models/find-ticket/find-ticket-store'
import formPassengersState from 'entities/beket/models/form-passengers/form-passengers-state'
import beketListTicketsStore from 'entities/beket/models/ticket/beket-list-tickets/beket-list-tickets-store'
import { setSettingForApp } from 'entities/user/module/app-settings'
import userStore from 'entities/user/module/user-store'

import { OrderApiBeket, ResponseReserveSeats } from 'shared/api/beket/order'
import { ShareKeys } from 'shared/constants'
import ErrorImage from 'shared/images/error.png'
import { getErrorMessage } from 'shared/lib/errorText'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'
import { Paper } from 'shared/ui/paper'
import { PopupContent } from 'shared/ui/poup-content'

import { SeatFree, SeatOccupied } from './component/cells'
import Pop from './component/pop'
import { SchemeHeader } from './component/scheme-header/scheme-header'
import { SchemeInfo } from './component/scheme-info'
import {
  type SchemeFloorRender,
  getBookedSeats,
  prepareSaloonFloors,
} from './libs/helper'
import s from './styles.module.scss'

const SeatTable = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const [loading, setLoading] = useState<boolean>(false)
  const [selected, setSelected] = useState<Array<number>>(
    formPassengersState?.selectedSeats || [],
  )

  const passengers = findTicketStore.formFindTicket

  const { selectedTicket, searchKey } = beketListTicketsStore

  const floors = useMemo(
    () => prepareSaloonFloors(selectedTicket?.saloon_scheme),
    [selectedTicket?.saloon_scheme],
  )

  const occupiedSeats = selectedTicket?.all_tickets

  const countSeats = findTicketStore.getCountOfPassengers()

  const [count, setCount] = useState(
    countSeats - (formPassengersState?.selectedSeats?.length || 0) || 0,
  )

  const bookedSeatNumbers = useMemo(
    () => new Set(occupiedSeats?.map((item) => item.seat_number) || []),
    [occupiedSeats],
  )

  const getIsBookedSeat = useMemo(
    () => getBookedSeats(bookedSeatNumbers),
    [bookedSeatNumbers],
  )

  const onClickCell = (cell?: number) => {
    if (!cell) return

    const index = selected?.findIndex((_cell) => cell === _cell)

    if (index >= 0) {
      setCount((prev) => prev + 1)

      setSelected((cells) => cells?.filter((_cell) => _cell !== cell))

      return
    }

    if (selected?.length < countSeats) {
      setCount((prev) => prev - 1)
      setSelected((cells) => [...(cells || []), cell])
    }
  }

  useEffect(() => {
    const raw = userStore.getClientStateParse<{
      formFindTicket?: typeof findTicketStore.formFindTicket
      selectedTicket?: typeof beketListTicketsStore.selectedTicket
    }>(ShareKeys.bus)
    if (
      !raw ||
      typeof raw !== 'object' ||
      !raw.formFindTicket ||
      !raw.selectedTicket
    )
      return

    findTicketStore.setFormFindTicket(raw.formFindTicket)
    beketListTicketsStore.setDetailTicket(raw.selectedTicket)
    void setSettingForApp({ key: ShareKeys.bus, value: '' })
  }, [userStore.client_state])

  const renderGrid = ({ layout, cellByPosition }: SchemeFloorRender) => {
    const rowStyle = {
      gridTemplateColumns: `repeat(${layout.horizontalValues.length || 1}, 1fr)`,
    }

    return layout.verticalValues.map((verticalValue) => {
      const rowCells = layout.horizontalValues.map((horizontalValue) => {
        const row =
          layout.verticalAxis === 'row' ? verticalValue : horizontalValue
        const col =
          layout.verticalAxis === 'col' ? verticalValue : horizontalValue

        const cellData = cellByPosition.get(`${row}:${col}`)
        const cellKey = `${row}-${col}`

        if (!cellData || cellData.type === 'empty') {
          return <div key={cellKey} />
        }

        if (getIsBookedSeat(cellData)) {
          return (
            <SeatOccupied
              {...cellData}
              key={cellKey}
            />
          )
        }

        const isClicked = cellData?.seat
          ? selected?.includes(cellData?.seat)
          : false

        return (
          <SeatFree
            {...cellData}
            key={cellKey}
            onClick={onClickCell}
            isClicked={isClicked}
          />
        )
      })

      return (
        <div
          key={verticalValue}
          className={s.row}
          style={rowStyle}
        >
          {rowCells}
        </div>
      )
    })
  }

  const onShowError = (errorMessage?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={errorMessage || t('please_tey_again')}
          button={t('try_again')}
          onClick={closeDialog}
          padding={[20]}
        />
      ),
      isDialog: false,
    })
  }

  const onContinue = (data: ResponseReserveSeats, isForMySelf?: boolean) => {
    closeDialog()

    formPassengersState.setSelectedSeats(selected)
    formPassengersState.setSeatsResponse(data, isForMySelf)

    navigate(RoutingPaths.beket.order.path())
  }

  const onClickContinue = async () => {
    if (!selectedTicket?.id) return

    setLoading(true)

    try {
      const { data: reversedSeats } = await OrderApiBeket.reverseSeats({
        seats: selected,
        searchkey: searchKey!,
        schedule_id: selectedTicket?.id,
        order_id: formPassengersState?.seatsResponse?.order_id || null,
        age_type: { adult: passengers?.adult || 1, child: passengers?.child },
      })

      if (formPassengersState?.formPassengers?.adult?.length) {
        onContinue(reversedSeats?.data)

        return
      }

      openDialog({
        child: (
          <Pop
            onContinue={(isForMySelf) =>
              onContinue(reversedSeats?.data, isForMySelf)
            }
          />
        ),
        isDialog: false,
      })
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={clsx('container', s.container)}>
      <HeaderGoBack
        variant="grey"
        title={t('select_seat')}
        buttonWhite
      />

      {loading ? (
        <Loader marginTop={110} />
      ) : (
        <>
          <SchemeInfo />

          <div className={clsx(!!count && s.marginBottom)}>
            <Paper padding={[16]}>
              <div className={s.seatTable}>
                <SchemeHeader countSeat={count} />

                {floors.map((floorItem) => (
                  <div
                    key={floorItem.floor}
                    className={s.floor}
                  >
                    {floors.length > 1 && (
                      <span className={clsx('semi-bold-14', s.floorTitle)}>
                        {t('beket.floor', { floor: floorItem.floor })}
                      </span>
                    )}

                    {renderGrid(floorItem)}
                  </div>
                ))}
              </div>
            </Paper>
          </div>

          {!count && (
            <ButtonContinue onClick={onClickContinue}>
              <span className={clsx('semi-bold-17', s.button)}>
                {t('continue')}
              </span>
            </ButtonContinue>
          )}
        </>
      )}
    </div>
  )
})

export default SeatTable
